import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import DashboardNav from "../components/DashboardNav";
import {
  ArrowLeft,
  Users,
  Phone,
  Mail,
  CreditCard,
  MonitorPlay,
  LinkIcon,
  Gamepad2,
  DollarSign,
  Eye,
  Clock,
  AlertCircle,
  BarChart3,
  ExternalLink,
  Globe,
  Youtube,
  Tv,
  Twitter,
  Instagram,
} from "lucide-react";

const PLATFORM_LABELS = {
  youtube: "YouTube",
  twitch: "Twitch",
  tiktok: "TikTok",
  twitter: "X / Twitter",
  instagram: "Instagram",
  kick: "Kick",
};

function AdminInfluencerDetailPage() {
  const { influencerId } = useParams();
  const navigate = useNavigate();

  const [influencer, setInfluencer] = useState(null);
  const [joinedCampaigns, setJoinedCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const userDoc = await getDoc(doc(db, "users", influencerId));
        if (!userDoc.exists()) {
          setLoading(false);
          return;
        }
        const userData = { id: userDoc.id, ...userDoc.data() };
        setInfluencer(userData);
        setIsApproved(!!userData.isApprovedByAdmin);

        const joinsSnap = await getDocs(
          query(
            collection(db, "campaignJoins"),
            where("influencerId", "==", influencerId)
          )
        );
        setJoinedCampaigns(
          joinsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const aTime = a.joinedAt?.seconds || 0;
              const bTime = b.joinedAt?.seconds || 0;
              return bTime - aTime;
            })
        );
      } catch (err) {
        console.error("Failed to fetch influencer:", err);
      }
      setLoading(false);
    }
    fetchData();
  }, [influencerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!influencer) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Influencer Not Found</h2>
          <button
            onClick={() => navigate("/admin")}
            className="text-brand-400 hover:text-brand-300 text-sm mt-2"
          >
            Back to Admin Panel
          </button>
        </div>
      </div>
    );
  }

  const profile = influencer.influencerProfile || {};
  const totalWishlists = joinedCampaigns.reduce((sum, j) => sum + (j.wishlists || 0), 0);
  const totalPurchases = joinedCampaigns.reduce((sum, j) => sum + (j.purchases || 0), 0);
  const totalVisits = joinedCampaigns.reduce((sum, j) => sum + (j.visits || 0), 0);
  const totalEarnings = joinedCampaigns.reduce((sum, j) => {
    if (j.tierSpend != null) return sum + j.tierSpend;
    const price = j.campaignType === "purchase" ? (j.pricePerPurchase || 0) : (j.pricePerWishlist || 0);
    const count = j.campaignType === "purchase" ? (j.purchases || 0) : (j.wishlists || 0);
    return sum + price * count;
  }, 0);

  const links = profile.links || {};
  const activeLinks = Object.entries(links).filter(([, url]) => url);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admin Panel
        </button>

        {/* Influencer Header */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 font-bold text-xl flex-shrink-0">
              {(influencer.displayName || influencer.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold">{influencer.displayName || "Unnamed"}</h1>
              <p className="text-gray-400 text-sm">{influencer.email}</p>
              {profile.username && (
                <p className="text-brand-400 text-sm font-medium mt-1">@{profile.username}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-3">
                <span className="text-xs bg-brand-500/10 text-brand-400 border border-brand-500/20 px-2.5 py-1 rounded-full">
                  Influencer
                </span>
                {influencer.utmId && (
                  <span className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">
                    UTM: {influencer.utmId}
                  </span>
                )}
                <span className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">
                  {joinedCampaigns.length} campaign{joinedCampaigns.length !== 1 ? "s" : ""} joined
                </span>
              </div>
            </div>
            {/* Approval Toggle */}
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <button
                onClick={async () => {
                  setToggling(true);
                  try {
                    const newVal = !isApproved;
                    await updateDoc(doc(db, "users", influencerId), { isApprovedByAdmin: newVal });
                    setIsApproved(newVal);
                    setInfluencer((prev) => ({ ...prev, isApprovedByAdmin: newVal }));
                    try {
                      const sendApprovalEmail = httpsCallable(functions, "sendApprovalEmail");
                      await sendApprovalEmail({ influencerId, approved: newVal });
                    } catch (emailErr) {
                      console.error("Failed to send approval email:", emailErr);
                    }
                  } catch (err) {
                    console.error("Failed to toggle approval:", err);
                  }
                  setToggling(false);
                }}
                disabled={toggling}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  isApproved ? "bg-green-500" : "bg-gray-700"
                } ${toggling ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    isApproved ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span className={`text-xs font-medium ${isApproved ? "text-green-400" : "text-gray-500"}`}>
                {isApproved ? "Approved" : "Not Approved"}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-brand-400">{totalWishlists.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Wishlists</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{totalVisits.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Visits</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-purple-400">{totalPurchases.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Purchases</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-green-400">${totalEarnings.toLocaleString()}</p>
            <p className="text-xs text-gray-500 mt-1">Total Earnings</p>
          </div>
        </div>

        {/* Profile Details */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Profile Details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Email */}
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Email</p>
                <p className="text-sm text-gray-200">{influencer.email}</p>
              </div>
            </div>

            {/* Username */}
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Username</p>
                <p className="text-sm text-gray-200">{profile.username || "—"}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Phone Number</p>
                <p className="text-sm text-gray-200">{profile.phoneNumber || "—"}</p>
              </div>
            </div>

            {/* Audience Size */}
            <div className="flex items-start gap-3">
              <BarChart3 className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Audience Size</p>
                <p className="text-sm text-gray-200">
                  {profile.audienceSize ? profile.audienceSize.toLocaleString() + "+" : "—"}
                </p>
              </div>
            </div>

            {/* Platforms */}
            <div className="flex items-start gap-3">
              <MonitorPlay className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Platforms</p>
                {profile.platforms && profile.platforms.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {profile.platforms.map((p) => (
                      <span key={p} className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full capitalize">
                        {PLATFORM_LABELS[p] || p}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-200">—</p>
                )}
              </div>
            </div>

            {/* Payment Method */}
            <div className="flex items-start gap-3">
              <CreditCard className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Payment</p>
                <p className="text-sm text-gray-200 capitalize">
                  {profile.paymentMethod || "—"}
                  {profile.paymentEmail && (
                    <span className="text-gray-500 ml-2">({profile.paymentEmail})</span>
                  )}
                  {profile.paymentMethod === "iban" && profile.paymentName && (
                    <>
                      <span className="text-gray-500 ml-2">({profile.paymentIban})</span>
                      <span className="text-gray-500 ml-2">({profile.paymentName})</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Joined Date */}
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Joined</p>
                <p className="text-sm text-gray-200">
                  {influencer.createdAt
                    ? new Date(influencer.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        {activeLinks.length > 0 && (
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold">Social Links</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeLinks.map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 hover:bg-gray-700 hover:border-gray-600 transition-all text-sm font-medium text-gray-300"
                >
                  {PLATFORM_LABELS[platform] || platform}
                  <ExternalLink className="w-3.5 h-3.5 text-gray-500" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Published Posts */}
        {(() => {
          const allPosts = joinedCampaigns
            .filter((j) => j.submittedPosts && j.submittedPosts.length > 0)
            .flatMap((j) =>
              j.submittedPosts.map((post) => ({
                ...post,
                campaignTitle: j.campaignTitle || j.campaignId,
                campaignId: j.campaignId,
              }))
            )
            .sort((a, b) => new Date(b.addedAt || 0) - new Date(a.addedAt || 0));

          if (allPosts.length === 0) return null;

          const platformIcons = { youtube: Youtube, twitch: Tv, twitter: Twitter, instagram: Instagram, tiktok: Globe, other: Globe };

          return (
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <ExternalLink className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold">Published Posts ({allPosts.length})</h2>
              </div>
              <div className="space-y-3">
                {allPosts.map((post, i) => {
                  const PlatformIcon = platformIcons[post.platform] || Globe;
                  return (
                    <div key={i} className="flex items-center gap-3 bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0">
                        <PlatformIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-gray-200 hover:text-brand-400 transition-colors truncate block"
                        >
                          {post.title || post.url}
                        </a>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Link
                            to={`/admin/campaigns/${post.campaignId}`}
                            className="text-xs text-gray-500 hover:text-brand-400 transition-colors"
                          >
                            {post.campaignTitle}
                          </Link>
                          {post.platform && (
                            <span className="text-xs text-gray-600 capitalize">{PLATFORM_LABELS[post.platform] || post.platform}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-gray-600 flex-shrink-0">
                        {post.addedAt ? new Date(post.addedAt).toLocaleDateString() : ""}
                      </span>
                      <a
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-white transition-colors flex-shrink-0"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Joined Campaigns */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Gamepad2 className="w-5 h-5 text-brand-400" />
            <h2 className="text-lg font-semibold">Joined Campaigns ({joinedCampaigns.length})</h2>
          </div>

          {joinedCampaigns.length === 0 ? (
            <div className="text-center py-8">
              <Gamepad2 className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No campaigns joined yet.</p>
            </div>
          ) : (
            <div className="border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-800/50 text-gray-400 text-xs">
                    <th className="text-left py-2.5 px-4 font-medium">Campaign</th>
                    <th className="text-right py-2.5 px-4 font-medium">Wishlists</th>
                    <th className="text-right py-2.5 px-4 font-medium">Visits</th>
                    <th className="text-right py-2.5 px-4 font-medium">Purchases</th>
                    <th className="text-right py-2.5 px-4 font-medium">Earnings</th>
                    <th className="text-right py-2.5 px-4 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {joinedCampaigns.map((join, i) => {
                    const earnings = join.tierSpend != null
                      ? join.tierSpend
                      : join.campaignType === "purchase"
                        ? (join.purchases || 0) * (join.pricePerPurchase || 0)
                        : (join.wishlists || 0) * (join.pricePerWishlist || 0);
                    return (
                      <tr
                        key={join.id}
                        className={`border-t border-gray-800/50 ${i % 2 === 0 ? "" : "bg-gray-900/30"}`}
                      >
                        <td className="py-2.5 px-4">
                          <Link
                            to={`/admin/campaigns/${join.campaignId}`}
                            className="text-sm font-medium text-gray-200 hover:text-brand-400 transition-colors"
                          >
                            {join.campaignTitle || join.campaignId}
                          </Link>
                          <p className="text-xs text-gray-600">{join.companyName || join.publisherName}</p>
                        </td>
                        <td className="text-right py-2.5 px-4 font-medium">
                          {(join.wishlists || 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2.5 px-4 text-gray-400">
                          {(join.visits || 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2.5 px-4 text-gray-400">
                          {(join.purchases || 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2.5 px-4 text-green-400">
                          ${earnings.toLocaleString()}
                        </td>
                        <td className="text-right py-2.5 px-4 text-gray-500 text-xs">
                          {join.joinedAt?.toDate
                            ? join.joinedAt.toDate().toLocaleDateString()
                            : join.joinedAt?.seconds
                            ? new Date(join.joinedAt.seconds * 1000).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminInfluencerDetailPage;
